/**
 * Admin API service
 * Handles authenticated requests to admin endpoints for tenant management
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';

/**
 * Get the current user's JWT token
 * @returns {Promise<string|null>}
 */
async function getAuthToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Custom error class for admin API errors
 */
export class AdminApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Make an authenticated admin API request
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
async function adminRequest(endpoint, options = {}) {
  const { method = 'GET', body, headers = {} } = options;

  const token = await getAuthToken();
  if (!token) {
    throw new AdminApiError('Not authenticated', 401);
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const requestHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...headers,
  };

  const fetchOptions = {
    method,
    headers: requestHeaders,
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new AdminApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData
    );
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// ============================================================================
// Tenant Management API
// ============================================================================

/**
 * List all tenants
 * @param {Object} params - Query parameters
 * @param {number} [params.limit] - Max items to return
 * @param {string} [params.nextToken] - Pagination token
 * @returns {Promise<{items: Array, nextToken?: string}>}
 */
export async function listTenants(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.set('limit', params.limit);
  if (params.nextToken) queryParams.set('nextToken', params.nextToken);

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `${API_ENDPOINTS.ADMIN_TENANTS}?${queryString}`
    : API_ENDPOINTS.ADMIN_TENANTS;

  return adminRequest(endpoint);
}

/**
 * Get a single tenant by ID
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<Object>} Tenant data
 */
export async function getTenant(tenantId) {
  return adminRequest(API_ENDPOINTS.ADMIN_TENANT_DETAIL(tenantId));
}

/**
 * Create a new tenant
 * @param {Object} tenantData - Tenant creation data
 * @param {string} tenantData.companyName - Company name
 * @param {string} tenantData.subdomain - Subdomain (lowercase, alphanumeric + hyphens)
 * @param {string} tenantData.industry - Industry type
 * @param {string} tenantData.tier - Subscription tier (free, pro)
 * @param {string} tenantData.adminEmail - Initial admin email
 * @param {string} tenantData.adminName - Initial admin name
 * @param {Object} [tenantData.settings] - Default settings
 * @param {Object} [tenantData.branding] - Branding configuration
 * @returns {Promise<Object>} Created tenant data
 */
export async function createTenant(tenantData) {
  return adminRequest(API_ENDPOINTS.ADMIN_TENANTS, {
    method: 'POST',
    body: tenantData,
  });
}

/**
 * Update an existing tenant
 * @param {string} tenantId - Tenant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated tenant data
 */
export async function updateTenant(tenantId, updates) {
  return adminRequest(API_ENDPOINTS.ADMIN_TENANT_DETAIL(tenantId), {
    method: 'PUT',
    body: updates,
  });
}

/**
 * Upload CSV file for a tenant's historical job data
 * @param {string} tenantId - Tenant ID
 * @param {File} file - CSV file to upload
 * @param {Function} [onProgress] - Progress callback (0-100)
 * @returns {Promise<Object>} Upload result
 */
export async function uploadTenantCsv(tenantId, file, onProgress) {
  const token = await getAuthToken();
  if (!token) {
    throw new AdminApiError('Not authenticated', 401);
  }

  const url = `${API_BASE_URL}${API_ENDPOINTS.ADMIN_TENANT_UPLOAD(tenantId)}`;

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          resolve({ success: true });
        }
      } else {
        let errorData = {};
        try {
          errorData = JSON.parse(xhr.responseText);
        } catch {
          // Ignore parse errors
        }
        reject(new AdminApiError(
          errorData.message || `Upload failed with status ${xhr.status}`,
          xhr.status,
          errorData
        ));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new AdminApiError('Network error during upload', 0));
    });

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', file);

    xhr.send(formData);
  });
}

// ============================================================================
// Public API (no auth required)
// ============================================================================

/**
 * Resolve tenant by subdomain (public endpoint)
 * @param {string} subdomain - Subdomain to look up
 * @returns {Promise<Object>} Tenant public info (branding, colors, etc.)
 */
export async function resolveTenantBySubdomain(subdomain) {
  const url = `${API_BASE_URL}${API_ENDPOINTS.PUBLIC_TENANT}?subdomain=${encodeURIComponent(subdomain)}`;

  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      return null; // Tenant not found
    }
    const errorData = await response.json().catch(() => ({}));
    throw new AdminApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
}

// ============================================================================
// Convenience export
// ============================================================================

export const adminApi = {
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  uploadTenantCsv,
  resolveTenantBySubdomain,
};

export default adminApi;
